import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  Zap,
  Layers,
  Cpu,
  Compass,
  ClipboardList,
  Rocket,
  Sparkles,
} from "lucide-react";

/* ---------- Motion primitives ---------- */
const maskLine = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.19, 1, 0.22, 1], delay: 0.1 + i * 0.12 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: i * 0.08 },
  }),
};

/* ---------- Hero image ---------- */
const HERO_IMG =
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=80";

const PILLARS = [
  {
    icon: Zap,
    tag: "01",
    title: "Agility",
    body: "Modular delivery pods, weekly value drops, and reversible decisions. We move at product-team speed inside enterprise guardrails.",
  },
  {
    icon: Layers,
    tag: "02",
    title: "Scalability",
    body: "Architectures designed for the tenth country roll-out, not just the first. Multi-tenant BTP, clean core, and shared services from day one.",
  },
  {
    icon: Cpu,
    tag: "03",
    title: "Intelligent Automation",
    body: "AI copilots, Joule, RPA, and process mining folded into the core — not bolted on. Automation is a design constraint, not an afterthought.",
  },
];

const STEPS = [
  {
    icon: Compass,
    n: "01",
    title: "Discovery",
    body: "Interviews, process mining and system X-ray. We surface the non-obvious constraints before scoping.",
    duration: "2 – 4 weeks",
  },
  {
    icon: ClipboardList,
    n: "02",
    title: "Blueprint",
    body: "Target architecture, clean-core boundary, integration canvas and a risk-mitigated cutover plan — all on one page.",
    duration: "4 – 8 weeks",
  },
  {
    icon: Rocket,
    n: "03",
    title: "Execution",
    body: "Fit-to-standard sprints led by senior consultants. Weekly demos, transparent burn, zero staffing pyramids.",
    duration: "16 – 36 weeks",
  },
  {
    icon: Sparkles,
    n: "04",
    title: "Optimization",
    body: "Post go-live hyper-care, value tracking, and continuous release management via our AMS practice.",
    duration: "Ongoing",
  },
];

const MARQUEE = [
  "S/4HANA",
  "Clean Core",
  "BTP",
  "SAP Joule",
  "Fiori UX",
  "Datasphere",
  "Signavio",
  "RISE with SAP",
  "GROW with SAP",
  "Integration Suite",
  "AI Copilots",
  "Process Mining",
];

export default function Approach() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div data-testid="approach-page" className="bg-[#FAFAFA]">
      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-[#0B1220] text-white"
        data-testid="approach-hero"
      >
        {/* soft gradient wash */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#C9A24E]/10 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#1E3A8A]/25 blur-[140px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-28 sm:pt-36 pb-24 sm:pb-32">
          <motion.div style={{ y: yTitle }} className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#C9A24E]"
              >
                <span className="w-8 h-px bg-[#C9A24E]" />
                Approach — Sunsri Software
              </motion.div>

              <h1 className="font-serif mt-8 text-[54px] sm:text-[84px] lg:text-[108px] leading-[0.95] tracking-[-0.02em]">
                {["Our", "Approach."].map((word, i) => (
                  <span key={word} className="block overflow-hidden">
                    <motion.span
                      custom={i}
                      variants={maskLine}
                      initial="hidden"
                      animate="show"
                      className="block"
                    >
                      {i === 1 ? (
                        <span className="italic text-[#C9A24E]">{word}</span>
                      ) : (
                        word
                      )}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <div className="mt-10 max-w-xl overflow-hidden">
                <motion.p
                  variants={maskLine}
                  custom={2}
                  initial="hidden"
                  animate="show"
                  className="text-lg sm:text-xl text-white/70 leading-relaxed"
                >
                  Bridging enterprise vision with intelligent, scalable execution.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="mt-14 flex items-center gap-6 text-xs uppercase tracking-[0.28em] text-white/50"
              >
                <span>Two sections</span>
                <span className="w-8 h-px bg-white/25" />
                <span>Seven principles</span>
                <span className="w-8 h-px bg-white/25 hidden sm:block" />
                <span className="hidden sm:inline">One outcome</span>
              </motion.div>
            </div>

            {/* Parallax hero image with clipped frame */}
            <div className="md:col-span-4 relative">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
                className="relative h-[420px] sm:h-[520px] overflow-hidden"
                style={{
                  clipPath: "polygon(0 6%, 100% 0, 100% 94%, 0 100%)",
                }}
              >
                <motion.img
                  src={HERO_IMG}
                  alt="Enterprise architecture spotlight"
                  style={{ y: yImg, scale: scaleImg }}
                  className="w-full h-full object-cover editorial-img"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220]/60 via-transparent to-[#0B1220]/20" />
                <div className="absolute bottom-6 left-6 right-6 text-[11px] uppercase tracking-[0.28em] text-white/70">
                  <div className="w-8 h-px bg-[#C9A24E] mb-3" />
                  Spotlight — Delivery in motion
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* slow editorial marquee */}
        <div className="relative border-t border-white/10 overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap py-8">
            {[...MARQUEE, ...MARQUEE].map((w, i) => (
              <span
                key={i}
                className="font-serif italic text-[52px] sm:text-[76px] leading-none px-10 text-white/15"
              >
                {w}
                <span className="text-[#C9A24E] mx-8">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CHAPTER 01 — Digital Transformation ============ */}
      <section
        className="relative py-28 sm:py-40 bg-[#FAFAFA]"
        data-testid="approach-chapter-1"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
            <div className="md:col-span-4">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="sticky top-32"
              >
                <div className="font-serif text-[120px] leading-none text-[#0F172A]/10 mt-4">
                  01
                </div>
              </motion.div>
            </div>

            <div className="md:col-span-8">
              <motion.h2
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="font-serif text-4xl sm:text-6xl leading-[1.05] text-[#0F172A] tracking-tight"
              >
                Digital transformation, <br />
                <span className="italic text-[#0F172A]/60">through innovation.</span>
              </motion.h2>

              <motion.p
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={1}
                className="text-lg text-gray-600 mt-8 max-w-xl leading-relaxed"
              >
                Emerging technologies, cloud-first strategies and clean-core architectures
                embedded directly into your business processes — not layered on top of them.
                We treat transformation as a product, not a project.
              </motion.p>
            </div>
          </div>

          {/* 3-column feature grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[#0F172A]/15">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.article
                  key={p.title}
                  data-testid={`pillar-${p.title.toLowerCase()}`}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={fadeUp}
                  custom={i}
                  whileHover={{ y: -6 }}
                  className={`group relative p-8 sm:p-10 border-b border-[#0F172A]/15 ${
                    i < 2 ? "md:border-r border-[#0F172A]/15" : ""
                  } transition-colors duration-500 hover:bg-[#0B1220] hover:text-white cursor-default`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-xs tracking-[0.28em] text-gray-500 group-hover:text-[#C9A24E] transition-colors">
                      {p.tag}
                    </span>
                    <Icon
                      size={28}
                      strokeWidth={1.2}
                      className="text-[#0F172A] group-hover:text-[#C9A24E] transition-colors"
                    />
                  </div>

                  <h3 className="font-serif text-3xl sm:text-4xl mt-16 leading-tight text-[#0F172A] group-hover:text-white transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] leading-relaxed text-gray-600 group-hover:text-white/70 transition-colors">
                    {p.body}
                  </p>

                  <div className="mt-10 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gray-400 group-hover:text-[#C9A24E] transition-colors">
                    <span className="w-6 h-px bg-current" />
                    Principle
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ CHAPTER 02 — Strategy to Execution ============ */}
      <section
        className="relative py-28 sm:py-40 bg-[#0B1220] text-white overflow-hidden"
        data-testid="approach-chapter-2"
      >
        <div className="absolute inset-0 pointer-events-none opacity-60">
          <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-[#C9A24E]/8 blur-[140px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-24">
            <div className="md:col-span-4">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="sticky top-32"
              >
                <div className="font-serif text-[120px] leading-none text-white/10 mt-4">
                  02
                </div>
              </motion.div>
            </div>
            <div className="md:col-span-8">
              <motion.h2
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="font-serif text-4xl sm:text-6xl leading-[1.05] tracking-tight"
              >
                From strategy to <br />
                <span className="italic text-[#C9A24E]">intelligent execution.</span>
              </motion.h2>
              <motion.p
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={1}
                className="text-lg text-white/70 mt-8 max-w-xl leading-relaxed"
              >
                A four-phase methodology for SAP S/4HANA — battle-tested across BFSI,
                manufacturing, retail and healthcare. Risk is mitigated by cadence,
                clarity and senior-led delivery.
              </motion.p>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* vertical rail */}
            <div className="absolute left-6 sm:left-10 top-0 bottom-0 w-px bg-white/15" />

            <div className="space-y-16 sm:space-y-24">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.n}
                    data-testid={`step-${s.n}`}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.9,
                      ease: [0.19, 1, 0.22, 1],
                      delay: i * 0.08,
                    }}
                    className="relative pl-16 sm:pl-24 group"
                  >
                    {/* node */}
                    <div className="absolute left-0 sm:left-4 top-2 w-12 h-12 -translate-x-[calc(50%-24px)] sm:-translate-x-[calc(50%-24px)]">
                      <div className="relative w-12 h-12 border border-white/30 flex items-center justify-center bg-[#0B1220] group-hover:border-[#C9A24E] transition-colors">
                        <Icon
                          size={18}
                          strokeWidth={1.4}
                          className="text-white/70 group-hover:text-[#C9A24E] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 sm:gap-10">
                      <div className="sm:col-span-3">
                        <div className="font-mono text-xs tracking-[0.28em] text-[#C9A24E]">
                          Phase {s.n}
                        </div>
                        <div className="font-serif text-5xl sm:text-6xl mt-3 leading-none text-white/95">
                          {s.n}
                        </div>
                        <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-white/45">
                          {s.duration}
                        </div>
                      </div>
                      <div className="sm:col-span-9">
                        <h3 className="font-serif text-3xl sm:text-4xl text-white">
                          {s.title}
                        </h3>
                        <p className="mt-5 text-[16px] leading-relaxed text-white/70 max-w-2xl">
                          {s.body}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA BAND ============ */}
      <section className="relative bg-[#FAFAFA] border-t border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
          >
            <div className="md:col-span-8">
              <div className="overline text-[#C9A24E]">Ready when you are</div>
              <h3 className="font-serif text-4xl sm:text-6xl mt-6 leading-[1.05] text-[#0F172A] tracking-tight">
                Let&apos;s design your <br />
                <span className="italic">SAP roadmap.</span>
              </h3>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/contact"
                data-testid="approach-cta"
                className="btn-gold"
              >
                Start with a discovery call <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
